import { db } from "../firebase/config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { generateNfcToken, getUserNfcTokens, revokeNfcToken } from "../firebase/nfcAuth";
import { isNFCSupported } from "./nfcService";

/**
 * Fetches NFC tokens for a specific user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of token objects
 */
export const fetchUserTokens = async (userId) => {
  if (!userId) return [];
  
  try {
    const userTokens = await getUserNfcTokens(userId);
    return userTokens;
  } catch (error) {
    console.error("Error fetching NFC tokens:", error);
    throw new Error("Failed to load NFC tokens");
  }
};

/**
 * Generates a new NFC token and URL for a user
 * @param {string} userId - The user ID
 * @param {Object} userData - User data object
 * @param {string} baseUrl - Base URL for the application
 * @returns {Promise<Object>} Object containing token and URL
 */
export const generateTokenAndUrl = async (userId, userData, baseUrl = "https://myneighbor.yiswork.com") => {
  try {
    // Check if the user has an nfcAuthSecret, if not, create one
    if (!userData?.nfcAuthSecret) {
      // Generate a simple password for NFC authentication
      const nfcAuthSecret = "nfc-" + Math.random().toString(36).substring(2, 10);

      // Update the user document with the NFC auth secret
      await updateDoc(doc(db, "users", userId), {
        nfcAuthSecret,
        nfcAuthEnabled: true,
      });
    }

    // Generate a token for the user
    const token = await generateNfcToken(userId);

    // Create the URL to program into the NFC card
    const nfcUrl = `${baseUrl}/auth?token=${token}`;
    
    return { token, nfcUrl };
  } catch (error) {
    console.error("Error generating token:", error);
    throw new Error(`Failed to generate token: ${error.message}`);
  }
};

/**
 * Fetches user data from Firestore
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} User data object
 */
export const fetchUserData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error("Error fetching user data:", error);
    throw new Error("Failed to fetch user data");
  }
};

/**
 * Revokes (deletes) an NFC token
 * @param {string} tokenId - The token ID to revoke
 * @returns {Promise<void>}
 */
export const revokeToken = async (tokenId) => {
  try {
    await revokeNfcToken(tokenId);
  } catch (error) {
    console.error("Error revoking token:", error);
    throw new Error("Failed to revoke token");
  }
};

/**
 * Determines the base URL for NFC authentication
 * @returns {string} The base URL
 */
export const determineBaseUrl = () => {
  if (window.location.origin.includes("localhost")) {
    return "https://myneighbor.yiswork.com";
  } else {
    return window.location.origin;
  }
};

/**
 * Checks if NFC is supported on the current device
 * @returns {boolean} Whether NFC is supported
 */
export const checkNfcSupport = () => {
  return isNFCSupported();
};